import { InputLabel, MenuItem } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo, useMemo } from 'react';
import { __ANPR_PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseListPayloadQuarkus } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { PmiSelect } from '../pmi-select';
import { PmiFormControl } from '../pmi-select/pmi-form-control';

interface CivilStatusListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (civilStatus?: string) => void;
  onChangeItem?: (itemSelected?: ItemVM) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
  nonClassificato?: boolean;
  filterItems?: (items: Array<ItemVM>) => Array<ItemVM>;
  helperText?: string;
}

type Key = {
  codice: string;
};

interface CivilStatus {
  key: Key;
  descrizione: string;
}

const CivilStatusListComponent = ({
  disabled,
  onChange,
  onChangeItem,
  value,
  required,
  error,
  label = 'Stato civile',
  id = 'civil-status-list',
  nonClassificato,
  filterItems,
  helperText,
}: CivilStatusListComponentProps): React.ReactElement => {
  const [civilStatus] = useHttpFetch<HttpResponseListPayloadQuarkus<CivilStatus>, Array<ItemVM>>(
    `${__ANPR_PERSON_NEW_API_URL__}/stato-civile`,
    {
      onBootstrap: true,
      initialData: [],
      options: {
        params: {
          filters: nonClassificato ? 'nonClassificato:1:EQUALS' : undefined,
        },
      },
      transformer: (response: AxiosResponse<HttpResponseListPayloadQuarkus<CivilStatus>>) => {
        const { data } = response;

        return data?.payload.map((item) => ({
          id: item.key.codice,
          value: item.descrizione,
        })) as Array<ItemVM>;
      },
    }
  );

  const list = useMemo(() => {
    let filteredItems = (civilStatus.data as Array<ItemVM>) || [];

    if (filterItems) {
      filteredItems = filterItems(filteredItems);
    }

    return filteredItems;
  }, [civilStatus.data, filterItems]);

  const idTag = `${id}-${label}`;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={idTag}>
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId={idTag}
        id={id}
        name={id}
        value={value}
        label={required ? `${label} *` : label}
        onChange={(event) => {
          const selectedValue = event.target.value;
          onChange(selectedValue);
          if (onChangeItem) {
            onChangeItem(list?.find((item) => item.id === selectedValue));
          }
        }}
        disabled={disabled}
        error={error}
        helperText={helperText}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {list.map((item: ItemVM) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

CivilStatusListComponent.displayName = 'Civil Status List component';

const CivilStatusList = memo(CivilStatusListComponent);

export default CivilStatusList as typeof CivilStatusListComponent;
